import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import axios from "axios";
import { VolunteersAboutService } from "../../services/volunteersAboutService";
import { VolunteersAboutValidator } from "../../validators/volunteersAboutValidator";
import { getErrorMessage } from "../../utils/errorUtils";
import {
	ApiResponse,
	getDefaultVolunteersAboutFormData,
	OperationStatus,
	toVolunteersAboutEditFormData,
	UpdateVolunteersAboutLocalizable,
	UpdateVolunteersAboutRequest,
	VOLUNTEERS_ABOUT_PATH,
	VolunteersAboutDetail,
	VolunteersAboutEditFormData,
	VolunteersAboutFormData,
	VolunteersAboutLoadState,
} from "./constants";

const NOT_FOUND_MESSAGE = "Koç Gönüllüleri Hakkında içeriği bulunamadı.";
const ALREADY_EXISTS_MESSAGE = "Koç Gönüllüleri Hakkında içeriği zaten mevcut. Mevcut içeriği düzenleyebilirsiniz.";

interface VolunteersAboutContextState {
	// Core Domain Data
	volunteersAbout: VolunteersAboutDetail | null;
	loadState: VolunteersAboutLoadState;
	volunteersAboutForm: VolunteersAboutFormData;
	volunteersAboutEditForm: VolunteersAboutEditFormData | null;
	operationStatus: OperationStatus;

	// Setters
	setVolunteersAboutForm: React.Dispatch<React.SetStateAction<VolunteersAboutFormData>>;
	setVolunteersAboutEditForm: React.Dispatch<React.SetStateAction<VolunteersAboutEditFormData | null>>;
	clearVolunteersAboutFormData: () => void;

	// Actions
	getVolunteersAbout: () => Promise<void>;
	redirectIfVolunteersAboutExists: () => Promise<void>;
	getVolunteersAboutForEdit: (aboutId: string) => Promise<void>;
	addVolunteersAbout: () => Promise<void>;
	updateVolunteersAbout: () => Promise<void>;
	removeVolunteersAbout: () => Promise<void>;
}

interface VolunteersAboutProviderProps {
	children: React.ReactNode;
}

const VolunteersAboutContext = createContext<VolunteersAboutContextState | undefined>(undefined);

export const useVolunteersAboutContext = () => {
	const context = useContext(VolunteersAboutContext);
	if (!context) {
		throw new Error("useVolunteersAboutContext must be used within a VolunteersAboutContextProvider");
	}
	return context;
};

const getResponseErrorMessage = (response: ApiResponse<unknown>) => {
	const error = response.error;
	if (typeof error === "string") {
		return error;
	}
	return error?.message || "Beklenmedik bir hata oluştu.";
};

const hasStatus = (error: unknown, status: number) => axios.isAxiosError(error) && error.response?.status === status;

const isNotFoundError = (error: unknown) => hasStatus(error, 404);

const showError = (error: unknown) => {
	console.error(error);
	if (isNotFoundError(error)) {
		message.error(NOT_FOUND_MESSAGE);
	} else if (hasStatus(error, 409)) {
		message.error(ALREADY_EXISTS_MESSAGE);
	} else {
		message.error(getErrorMessage(error));
	}
};

/* -------------- Build Update Request (Only Changed Fields) -------------- */

const normaliseText = (value: string) => value.replace(/\r\n?/g, "\n").trim();

export const buildVolunteersAboutUpdateRequest = (
	current: VolunteersAboutEditFormData,
	original: VolunteersAboutEditFormData
): UpdateVolunteersAboutRequest => ({
	volunteersAboutLocalizable: current.volunteersAboutLocalizable.flatMap((item): UpdateVolunteersAboutLocalizable[] => {
		const before = original.volunteersAboutLocalizable.find(o => o.culture === item.culture);
		// Browsers send line breaks as \r\n and show them as \n, so only the text itself is compared.
		const isChanged = (value: string, previous = "") => normaliseText(value) !== normaliseText(previous);
		const changes = {
			title: isChanged(item.title, before?.title) ? item.title : undefined,
			description: isChanged(item.description, before?.description) ? item.description : undefined,
			image: item.image ?? undefined,
		};

		if (Object.values(changes).every(value => value === undefined)) {
			return [];
		}
		return [{ volunteersAboutLocalizableId: item.volunteersAboutLocalizableId, culture: item.culture, ...changes }];
	}),
});

export const VolunteersAboutContextProvider: React.FC<VolunteersAboutProviderProps> = ({ children }) => {
	const navigate = useNavigate();
	const [volunteersAbout, setVolunteersAbout] = useState<VolunteersAboutDetail | null>(null);
	const [loadState, setLoadState] = useState<VolunteersAboutLoadState>("loading");
	const [volunteersAboutForm, setVolunteersAboutForm] = useState<VolunteersAboutFormData>(getDefaultVolunteersAboutFormData);
	const [volunteersAboutEditForm, setVolunteersAboutEditForm] = useState<VolunteersAboutEditFormData | null>(null);
	const [operationStatus, setOperationStatus] = useState<OperationStatus>({ type: "idle" });
	// The loading state only disables the buttons after a re-render, so a fast double click could still send a
	// second request; this ref blocks overlapping add/update/delete calls synchronously.
	const isMutatingRef = useRef(false);

	const backToOverview = useCallback(() => navigate(VOLUNTEERS_ABOUT_PATH, { replace: true }), [navigate]);

	/* -------------- Clear helpers -------------- */

	const clearVolunteersAboutFormData = useCallback(() => {
		setVolunteersAboutForm(getDefaultVolunteersAboutFormData());
		setVolunteersAboutEditForm(null);
	}, []);

	/* -------------- Actions -------------- */

	// The overview: the stored content, or the "missing" state when the GET answers 404.
	const getVolunteersAbout = useCallback(async () => {
		setLoadState("loading");
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await VolunteersAboutService.getVolunteersAbout();

			if (response.isSuccessful) {
				setVolunteersAbout(response.result);
				setLoadState("ready");
			} else {
				message.error(getResponseErrorMessage(response));
				setLoadState("error");
			}
		} catch (error) {
			setVolunteersAbout(null);
			if (isNotFoundError(error)) {
				setLoadState("missing");
			} else {
				showError(error);
				setLoadState("error");
			}
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, []);

	// There is only one content: the add screen sends the user to the overview when it already exists.
	const redirectIfVolunteersAboutExists = useCallback(async () => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await VolunteersAboutService.getVolunteersAbout();
			if (response.isSuccessful) {
				message.info(ALREADY_EXISTS_MESSAGE);
				backToOverview();
			}
		} catch (error) {
			// 404 is the expected answer here; any other failure is left to the add request itself.
			if (!isNotFoundError(error)) {
				console.error(error);
			}
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [backToOverview]);

	// The edit address carries the id of the content it was opened for; a deleted or replaced content is not edited.
	const getVolunteersAboutForEdit = useCallback(async (aboutId: string) => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await VolunteersAboutService.getVolunteersAbout();

			if (response.isSuccessful && response.result.aboutId === aboutId) {
				setVolunteersAbout(response.result);
				setVolunteersAboutEditForm(toVolunteersAboutEditFormData(response.result));
			} else {
				message.error(response.isSuccessful ? NOT_FOUND_MESSAGE : getResponseErrorMessage(response));
				backToOverview();
			}
		} catch (error) {
			showError(error);
			if (isNotFoundError(error)) {
				backToOverview();
			}
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [backToOverview]);

	const addVolunteersAbout = useCallback(async () => {
		if (isMutatingRef.current) {
			return;
		}

		const validationError = VolunteersAboutValidator.validateAddVolunteersAbout(volunteersAboutForm);
		if (validationError) {
			message.info(validationError);
			return;
		}

		isMutatingRef.current = true;
		setOperationStatus({ type: "loading", operation: "add" });

		try {
			const response = await VolunteersAboutService.addVolunteersAbout(volunteersAboutForm);

			if (response.isSuccessful) {
				message.success("Koç Gönüllüleri Hakkında içeriği başarıyla eklendi.");
				setVolunteersAboutForm(getDefaultVolunteersAboutFormData());
				backToOverview();
			} else {
				message.error(getResponseErrorMessage(response));
			}
		} catch (error) {
			showError(error);
			if (hasStatus(error, 409)) {
				backToOverview();
			}
		} finally {
			isMutatingRef.current = false;
			setOperationStatus({ type: "idle" });
		}
	}, [volunteersAboutForm, backToOverview]);

	const updateVolunteersAbout = useCallback(async () => {
		if (isMutatingRef.current) {
			return;
		}
		if (!volunteersAboutEditForm || !volunteersAbout) {
			message.error(NOT_FOUND_MESSAGE);
			return;
		}

		const updateRequest = buildVolunteersAboutUpdateRequest(volunteersAboutEditForm, toVolunteersAboutEditFormData(volunteersAbout));

		const validationError = VolunteersAboutValidator.validateUpdateVolunteersAbout(updateRequest);
		if (validationError) {
			message.info(validationError);
			return;
		}

		if (updateRequest.volunteersAboutLocalizable.length === 0) {
			message.info("Herhangi bir değişiklik yapılmadı.");
			return;
		}

		isMutatingRef.current = true;
		setOperationStatus({ type: "loading", operation: "update" });

		try {
			const response = await VolunteersAboutService.updateVolunteersAbout(updateRequest);

			if (response.isSuccessful) {
				message.success("Koç Gönüllüleri Hakkında içeriği başarıyla güncellendi.");
				clearVolunteersAboutFormData();
				backToOverview();
			} else {
				message.error(getResponseErrorMessage(response));
			}
		} catch (error) {
			showError(error);
			if (isNotFoundError(error)) {
				backToOverview();
			}
		} finally {
			isMutatingRef.current = false;
			setOperationStatus({ type: "idle" });
		}
	}, [volunteersAboutEditForm, volunteersAbout, backToOverview, clearVolunteersAboutFormData]);

	const removeVolunteersAbout = useCallback(async () => {
		if (isMutatingRef.current) {
			return;
		}

		isMutatingRef.current = true;
		setOperationStatus({ type: "loading", operation: "delete" });

		try {
			const response = await VolunteersAboutService.removeVolunteersAbout();

			if (response.isSuccessful) {
				message.success("Koç Gönüllüleri Hakkında içeriği başarıyla silindi.");
			} else {
				message.error(getResponseErrorMessage(response));
			}
		} catch (error) {
			showError(error);
		} finally {
			isMutatingRef.current = false;
			setOperationStatus({ type: "idle" });
		}
	}, []);

	const contextValue = useMemo<VolunteersAboutContextState>(
		() => ({
			volunteersAbout,
			loadState,
			volunteersAboutForm,
			volunteersAboutEditForm,
			operationStatus,
			setVolunteersAboutForm,
			setVolunteersAboutEditForm,
			clearVolunteersAboutFormData,
			getVolunteersAbout,
			redirectIfVolunteersAboutExists,
			getVolunteersAboutForEdit,
			addVolunteersAbout,
			updateVolunteersAbout,
			removeVolunteersAbout,
		}),
		[
			volunteersAbout,
			loadState,
			volunteersAboutForm,
			volunteersAboutEditForm,
			operationStatus,
			clearVolunteersAboutFormData,
			getVolunteersAbout,
			redirectIfVolunteersAboutExists,
			getVolunteersAboutForEdit,
			addVolunteersAbout,
			updateVolunteersAbout,
			removeVolunteersAbout,
		]
	);

	return <VolunteersAboutContext.Provider value={contextValue}>{children}</VolunteersAboutContext.Provider>;
};
